import { apolloClient } from "@App/graphql/client";
import type { FleetErrorCode } from "@App/apis/graphql/fleet/errors";
import { GET_VEHICLE, type GetVehicleResponse } from "@App/apis/graphql/fleet/vehicle/queries";
import type { Brand, Generation, Motorization } from "@App/types";

export interface FleetVehicle {
  brand: Brand | null;
  generation: Generation | null;
  motorization: Motorization | null;
  isBev: boolean;
}

export type GetVehicleResult =
  | {
      ok: false;
      error: FleetErrorCode;
    }
  | {
      ok: true;
      vehicle: FleetVehicle;
    };

export interface GetVehiclePayload {
  organizationId: string;
  contractNumber: string;
}

/**
 * Looks up the vehicle belonging to a fleet contract number.
 *
 * The backend signals an unknown contract by throwing rather than by returning
 * a reason code, and it has no other documented failure mode, so every error is
 * reported as `contract_not_found`. Nothing is distinguishable beyond that.
 *
 * The response nests everything below `keyNumber` and names the
 * generation-bearing node `type` — that node actually is the motorization, so
 * it is mapped back to the vehicle tree wording used across the frontend.
 */
async function getVehicle({
  organizationId,
  contractNumber,
}: GetVehiclePayload): Promise<GetVehicleResult> {
  let data: GetVehicleResponse | null | undefined;
  try {
    const result = await apolloClient.query<GetVehicleResponse>({
      query: GET_VEHICLE,
      variables: { organizationId, contractNumber },
    });
    data = result.data;
  } catch {
    return { ok: false, error: "contract_not_found" };
  }

  const keyNumber = data?.organization?.Fleet?.GetVehicle?.keyNumber;
  const motorization = keyNumber?.type;
  if (!keyNumber || !motorization) {
    return { ok: false, error: "contract_not_found" };
  }

  return {
    ok: true,
    vehicle: {
      brand: keyNumber.brand
        ? {
            id: keyNumber.brand.id,
            name: keyNumber.brand.name,
            slug: keyNumber.brand.slug ?? undefined,
            logo: keyNumber.brand.logo ?? "",
          }
        : null,
      generation: motorization.generation
        ? {
            id: motorization.generation.id,
            name: motorization.generation.name,
            slug: motorization.generation.slug ?? undefined,
          }
        : null,
      // The fleet lookup only exposes the identity of the motorization, the
      // remaining vehicle tree attributes (kw, displacement, …) are not part of
      // the response.
      motorization: {
        id: motorization.id,
        name: motorization.name,
      } as Motorization,
      isBev: motorization.isBev ?? false,
    },
  };
}

export const vehicle = {
  getVehicle,
};
